import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../../services/api';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowRightIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import { KPICardSkeleton, ChartSkeleton, ListSkeleton } from '../../components/UI/Skeleton';
import UserAvatar from '../../components/UI/UserAvatar';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  new: '#3b82f6', contacted: '#f59e0b', qualified: '#10b981',
  proposal: '#8b5cf6', negotiation: '#f97316', converted: '#059669', lost: '#ef4444',
};
const STAGE_COLORS = {
  prospecting: '#3b82f6', qualification: '#f59e0b', proposal: '#8b5cf6',
  negotiation: '#f97316', closed_won: '#10b981', closed_lost: '#ef4444',
};
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f97316', '#ef4444', '#06b6d4', '#ec4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-surface-800 px-4 py-3 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs text-gray-600 dark:text-gray-300">
            {entry.name}: <span className="font-medium">{typeof entry.value === 'number' && entry.value > 999 ? `$${entry.value.toLocaleString()}` : entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-surface-800 px-4 py-3 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{payload[0].name}</p>
        <p className="text-xs text-gray-600 dark:text-gray-300">Count: <span className="font-medium">{payload[0].value}</span></p>
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user } = useAuth();
  const { data: dashboardData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => { const r = await dashboardAPI.getStats(); return r.data.data; },
  });

  const stats = dashboardData?.overview;
  const chartData = {
    leadsByStatus: dashboardData?.leads?.byStatus || [],
    dealsByStage: dashboardData?.deals?.byStage || [],
    leadsBySource: dashboardData?.leads?.bySource || [],
  };
  const recentLeads = dashboardData?.leads?.recent || [];
  const recentDeals = dashboardData?.deals?.recent || [];
  const teamPerformance = dashboardData?.teamPerformance;
  const pipeline = dashboardData?.pipeline;
  const trends = dashboardData?.trends || [];

  const activeLeads = chartData.leadsByStatus
    .filter(l => !['converted', 'lost'].includes(l._id))
    .reduce((sum, l) => sum + l.count, 0);
  const activeDeals = chartData.dealsByStage
    .filter(d => !['closed_won', 'closed_lost'].includes(d._id))
    .reduce((sum, d) => sum + d.count, 0);

  const kpiCards = [
    { title: 'Active Leads', value: activeLeads, total: stats?.totalLeads, icon: UserGroupIcon, iconBg: 'bg-blue-50 dark:bg-blue-500/10', iconColor: 'text-blue-600 dark:text-blue-400', href: '/leads' },
    { title: 'Active Deals', value: activeDeals, total: stats?.totalDeals, icon: BriefcaseIcon, iconBg: 'bg-emerald-50 dark:bg-emerald-500/10', iconColor: 'text-emerald-600 dark:text-emerald-400', href: '/deals' },
    { title: 'Won Revenue', value: `$${(stats?.totalWonRevenue || 0).toLocaleString()}`, sub: `Win Rate: ${stats?.winRate || 0}%`, icon: TrophyIcon, iconBg: 'bg-violet-50 dark:bg-violet-500/10', iconColor: 'text-violet-600 dark:text-violet-400', href: '/deals' },
    { title: 'Pipeline Value', value: `$${(pipeline?.totalValue || 0).toLocaleString()}`, sub: `Weighted: $${(pipeline?.weightedValue || 0).toLocaleString()}`, icon: CurrencyDollarIcon, iconBg: 'bg-amber-50 dark:bg-amber-500/10', iconColor: 'text-amber-600 dark:text-amber-400', href: '/deals' },
    { title: 'Potential Volume', value: (stats?.totalLeadPotentialVolume || 0).toLocaleString(), sub: 'Total across all leads', icon: ArrowTrendingUpIcon, iconBg: 'bg-indigo-50 dark:bg-indigo-500/10', iconColor: 'text-indigo-600 dark:text-indigo-400', href: '/leads' },
  ];

  const leadDistributionData = chartData.leadsByStatus.map(item => ({
    name: item._id.charAt(0).toUpperCase() + item._id.slice(1), value: item.count, color: STATUS_COLORS[item._id] || '#6b7280',
  }));
  const dealPipelineData = chartData.dealsByStage.map(item => ({
    name: item._id.charAt(0).toUpperCase() + item._id.slice(1).replace('_', ' '),
    deals: item.count, value: item.value || 0, color: STAGE_COLORS[item._id] || '#6b7280',
  }));

  const SOURCE_COLORS = { website: '#3b82f6', referral: '#10b981', linkedin: '#0077b5', cold_call: '#f59e0b', email: '#8b5cf6', discovery: '#f97316', social_media: '#ec4899', trade_show: '#06b6d4', other: '#6b7280' };
  const leadSourceData = chartData.leadsBySource.map(item => ({
    name: (item._id || 'Unknown').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: item.count,
    color: SOURCE_COLORS[item._id] || CHART_COLORS[chartData.leadsBySource.indexOf(item) % CHART_COLORS.length],
  }));

  // Conversion funnel data
  const conversionRate = stats?.conversionRate || 0;
  const winRate = stats?.winRate || 0;

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
  const itemVariants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Page Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {getGreeting()}, {user?.firstName || 'there'}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Here's your sales overview for today.</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statsLoading ? <KPICardSkeleton count={5} /> : kpiCards.map((card, index) => (
          <Link key={index} to={card.href}>
            <motion.div variants={itemVariants} whileHover={{ y: -4, scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className="card-premium rounded-2xl p-5 group cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <motion.div whileHover={{ scale: 1.15, rotate: -8 }} transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                  className={`${card.iconBg} p-2.5 rounded-xl shadow-sm`}>
                  <card.icon className={`w-5 h-5 ${card.iconColor}`} />
                </motion.div>
                <ArrowRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary-500 group-hover:translate-x-1 transition-all duration-300" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{card.title}</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</h3>
              {card.total && <p className="text-xs text-gray-400 mt-1">{card.total} total</p>}
              {card.sub && <p className="text-xs text-gray-400 mt-1">{card.sub}</p>}
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lead Distribution */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Lead Distribution</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Breakdown by current status</p>
          {statsLoading ? <ChartSkeleton height={280} /> : leadDistributionData.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No lead data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={leadDistributionData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {leadDistributionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Deal Pipeline */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Deal Pipeline</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Value by stage</p>
          {statsLoading ? <ChartSkeleton height={280} /> : dealPipelineData.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No deal data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dealPipelineData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} angle={-45} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="value" name="Value" radius={[6, 6, 0, 0]} maxBarSize={48}>
                  {dealPipelineData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>

      {/* Monthly Trends + Lead Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Activity Trend */}
        <motion.div variants={itemVariants} className="lg:col-span-2 card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Monthly Activity</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Leads, deals & revenue over last 6 months</p>
          {statsLoading ? <ChartSkeleton height={280} /> : trends.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No trend data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="gradLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradDeals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="leads" name="Leads" stroke="#3b82f6" strokeWidth={2} fill="url(#gradLeads)" dot={{ r: 3, fill: '#3b82f6' }} />
                <Area type="monotone" dataKey="deals" name="Deals" stroke="#10b981" strokeWidth={2} fill="url(#gradDeals)" dot={{ r: 3, fill: '#10b981' }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Lead Sources */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Lead Sources</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Where your leads come from</p>
          {statsLoading ? <ChartSkeleton height={280} /> : leadSourceData.length === 0 ? (
            <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm">No source data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={leadSourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {leadSourceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>

      {/* Revenue Trend */}
      <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <ArrowTrendingUpIcon className="w-4 h-4 text-emerald-500" />Revenue Won
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Closed-won deal revenue by month</p>
        {statsLoading ? <ChartSkeleton height={220} /> : trends.every(t => t.revenue === 0) ? (
          <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">No revenue data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trends} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </motion.div>

      {/* Conversion Rates + Team Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Conversion Rates */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Conversion Rates</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Lead conversion & deal win rate</p>
          {statsLoading ? <ChartSkeleton height={200} /> : (
            <div className="space-y-6">
              {[
                { label: 'Lead Conversion', value: conversionRate, color: '#3b82f6', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
                { label: 'Deal Win Rate', value: winRate, color: '#10b981', bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' },
              ].map(item => (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{item.value}%</span>
                  </div>
                  <div className={`h-3 rounded-full ${item.bgColor} overflow-hidden`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(item.value, 100)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-surface-700">
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <p className="text-xs text-gray-500">Total Leads</p>
                  <p className="text-lg font-bold text-blue-600">{stats?.totalLeads || 0}</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                  <p className="text-xs text-gray-500">Total Deals</p>
                  <p className="text-lg font-bold text-emerald-600">{stats?.totalDeals || 0}</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Team: Active Leads per Member */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <UserGroupIcon className="w-4 h-4 text-blue-500" />Active Leads per Member
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Excluding converted & lost</p>
          {statsLoading ? <ListSkeleton items={4} /> : !teamPerformance?.leadsPerMember?.length ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No data</div>
          ) : (
            <div className="space-y-3">
              {teamPerformance.leadsPerMember.map((member, i) => (
                <div key={member._id || i} className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-800/20 text-xs font-bold text-blue-600 flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : 'Unassigned'}
                    </p>
                    <div className="mt-1 h-2 rounded-full bg-blue-100 dark:bg-blue-900/30 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min((member.count / (teamPerformance.leadsPerMember[0]?.count || 1)) * 100, 100)}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1 }} className="h-full rounded-full bg-blue-500" />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-blue-600 flex-shrink-0">{member.count}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Team: Active Deals per Member */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <BriefcaseIcon className="w-4 h-4 text-emerald-500" />Active Deals per Member
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Excluding closed deals</p>
          {statsLoading ? <ListSkeleton items={4} /> : !teamPerformance?.dealsPerMember?.length ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No data</div>
          ) : (
            <div className="space-y-3">
              {teamPerformance.dealsPerMember.map((member, i) => (
                <div key={member._id || i} className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-800/20 text-xs font-bold text-emerald-600 flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : 'Unassigned'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min((member.count / (teamPerformance.dealsPerMember[0]?.count || 1)) * 100, 100)}%` }}
                          transition={{ duration: 0.8, delay: i * 0.1 }} className="h-full rounded-full bg-emerald-500" />
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-emerald-600">{member.count}</span>
                    <p className="text-xs text-gray-400">${(member.totalValue || 0).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Leads */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Leads</h2>
            <Link to="/leads" className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1">View all <ArrowRightIcon className="w-3 h-3" /></Link>
          </div>
          <div className="p-2">
            {statsLoading ? <div className="p-4"><ListSkeleton items={5} /></div> : recentLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400"><UserGroupIcon className="w-8 h-8 mb-2" /><p className="text-sm">No recent leads</p></div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-700/30">
                {recentLeads.map((lead, idx) => (
                  <Link key={lead._id} to={`/leads/${lead._id}`}>
                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
                      className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-surface-700/50 transition-all cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <UserAvatar user={lead.assignedTo || lead} size="md" />
                        <div><p className="text-sm font-medium text-gray-900 dark:text-white">{lead.firstName} {lead.lastName}</p><p className="text-xs text-gray-500 dark:text-gray-400">{lead.email}</p></div>
                      </div>
                      <div className="text-right">
                        <span className={`badge ${lead.status === 'new' ? 'badge-info' : lead.status === 'qualified' ? 'badge-success' : lead.status === 'contacted' ? 'badge-warning' : 'badge-default'}`}>{lead.status}</span>
                        <p className="text-2xs text-gray-400 mt-1">{new Date(lead.createdAt).toLocaleDateString()}</p>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Deals */}
        <motion.div variants={itemVariants} className="card-premium rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Recent Deals</h2>
            <Link to="/deals" className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1">View all <ArrowRightIcon className="w-3 h-3" /></Link>
          </div>
          <div className="p-2">
            {statsLoading ? <div className="p-4"><ListSkeleton items={5} /></div> : recentDeals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400"><BriefcaseIcon className="w-8 h-8 mb-2" /><p className="text-sm">No recent deals</p></div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-700/30">
                {recentDeals.map((deal, idx) => (
                  <Link key={deal._id} to={`/deals/${deal._id}`}>
                    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }}
                      className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-surface-700/50 transition-all cursor-pointer">
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{deal.name}</p><p className="text-xs text-gray-500 dark:text-gray-400">{deal.customer?.name || 'No customer'}</p></div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">${deal.value?.toLocaleString()}</p>
                        <span className={`badge ${deal.stage === 'closed_won' ? 'badge-success' : deal.stage === 'negotiation' ? 'badge-warning' : deal.stage === 'closed_lost' ? 'badge-danger' : 'badge-info'}`}>{deal.stage?.replace('_', ' ')}</span>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard;
